import { FC, Fragment, ReactElement, useMemo, useState } from 'react';

import { VenmoLogo } from '@assets/VenmoLogo';

import { ProfileLink } from '@queries/EntertainerQueries';

import { useAnalytics } from '@hooks/useAnalytics';

import { openExternalUrl } from '@utilities/openExternalUrl';

import Button from '@components/buttons/Button';
import IconButton from '@components/buttons/IconButton';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import { useDialog } from '@components/layout/Dialog/useDialog';
import { HorizontalScroll } from '@components/layout/HorizontalScroll/HorizontalScroll';

import { AddProfileSocialLinkPopup } from './AddProfileSocialLinkPopup';

const RemoveButton = ({ onRemove }: { onRemove: () => Promise<void> }) => {
  return (
    <div style={{ position: 'absolute', top: -4, right: -4 }}>
      <IconButton
        color="neutral"
        onClick={onRemove}
        style={{ width: 14, height: 14, fontSize: 10 }}
      >
        <FontAwesomeIcon name="times" />
      </IconButton>
    </div>
  );
};

const LINK_ICON_RECORD: Record<string, ReactElement> = {
  amazonWishlist: <FontAwesomeIcon faStyle="fab" name="amazon" />,
  venmo: (
    <div style={{ width: 16, height: 16 }}>
      <VenmoLogo />
    </div>
  ),
  instagram: <FontAwesomeIcon faStyle="fab" name="instagram" />,
  tiktok: <FontAwesomeIcon faStyle="fab" name="tiktok" />,
  email: <FontAwesomeIcon faStyle="far" name="envelope" />,
};

interface ProfileLinkButtonProps {
  link: ProfileLink & { key?: string };
  onRemoveLink: (link: ProfileLink) => Promise<void>;
}

const ProfileLinkButton = ({ link, onRemoveLink }: ProfileLinkButtonProps) => {
  const { track } = useAnalytics();
  const { openConfirmDialog } = useDialog();
  const [didRemove, setDidRemove] = useState(false);

  const handleRemoveLink = async () => {
    openConfirmDialog({
      title: `Remove "${link.text}"?`,
      text: 'Are you sure you want to remove this link?',
      onConfirm: async () => {
        track('Remove Link', {
          type: link.type,
          url: link.url,
        });

        setDidRemove(true);
        await onRemoveLink(link);
      },
      buttonOk: 'Remove',
    });
  };

  if (didRemove) {
    return <></>;
  }

  return (
    <div className="flex-grow-0" style={{ position: 'relative' }}>
      {link.key && <RemoveButton onRemove={handleRemoveLink} />}
      <Button
        startIcon={LINK_ICON_RECORD[link.type]}
        className="py-1 px-3"
        size="sm"
        variant="outlined"
        color="neutral"
        roundness="rounded"
        onClick={() => {
          track('Click Entertainer Link', {
            type: link.type,
            url: link.url,
          });
          openExternalUrl(link.url);
        }}
        style={{ height: 30 }}
      >
        <span style={{ whiteSpace: 'nowrap' }}>{link.text}</span>
      </Button>
    </div>
  );
};

export const ProfileLinksSection: FC<{
  canAddLinks: boolean;
  links: ProfileLink[];
  onSubmitUpdatedLinks: (links: ProfileLink[]) => Promise<void>;
  onRemoveLink: (link: ProfileLink) => Promise<void>;
}> = ({ canAddLinks, links, onSubmitUpdatedLinks, onRemoveLink }) => {
  const [addProfileSocialLinkPopupIsVisible, setAddProfileSocialLinkPopupIsVisible] =
    useState(false);
  const { track } = useAnalytics();

  const handleAddLink = () => {
    track('Click Add Link');
    setAddProfileSocialLinkPopupIsVisible(true);
  };

  if (!links.length && !canAddLinks) {
    return <></>;
  }

  return (
    <div className="d-flex" style={{ margin: '-8px 0' }}>
      <HorizontalScroll
        items={[
          <div className="d-flex gap-1" style={{ padding: '8px 0' }}>
            {links.map((link) => (
              <Fragment key={link.type}>
                <ProfileLinkButton link={link} onRemoveLink={onRemoveLink} />
              </Fragment>
            ))}
            {canAddLinks && (
              <div className="flex-grow-0">
                <Button
                  startIcon={<FontAwesomeIcon name="plus" />}
                  className="py-1 px-3"
                  size="sm"
                  variant="outlined"
                  color="neutral"
                  roundness="rounded"
                  style={{ height: 30, border: '1px dashed #fff' }}
                  onClick={handleAddLink}
                >
                  <span style={{ whiteSpace: 'nowrap' }}>Add Link</span>
                </Button>
              </div>
            )}
          </div>,
        ]}
      />
      <AddProfileSocialLinkPopup
        isVisible={addProfileSocialLinkPopupIsVisible}
        onClose={() => setAddProfileSocialLinkPopupIsVisible(false)}
        onSubmit={onSubmitUpdatedLinks}
      />
    </div>
  );
};
